import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiActivity, FiClock, FiCheck, FiX, FiPlay, FiStopCircle } from 'react-icons/fi';

import { batchGenerationsApi } from '@/services/batchGenerations';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

// Polling interval (ms). Sucker Punch roda inline, então mudanças aparecem rápido.
const POLL_INTERVAL = 3000;

const STATUS_CONFIG = {
  queued:   { label: 'Na fila',  variant: 'neutral-lightest', icon: <FiClock /> },
  running:  { label: 'Rodando',  variant: 'warning',          icon: <FiPlay /> },
  done:     { label: 'Concluído', variant: 'success',         icon: <FiCheck /> },
  failed:   { label: 'Falhou',   variant: 'danger',           icon: <FiX /> },
  canceled: { label: 'Cancelado', variant: 'neutral-lightest', icon: <FiX /> }
};

const ACTIVE_STATUSES = ['queued', 'running'];

const TAB_DEFS = [
  { value: 'active',   label: 'Em andamento', statuses: ['queued', 'running'] },
  { value: 'done',     label: 'Concluídos',   statuses: ['done'] },
  { value: 'canceled', label: 'Cancelados',   statuses: ['canceled'] },
  { value: 'failed',   label: 'Falhas',       statuses: ['failed'] }
];

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

function elapsedMinutes(startedAt) {
  if (!startedAt) return null;
  const ms = Date.now() - new Date(startedAt).getTime();
  return Math.max(0, Math.round(ms / 60000));
}

/**
 * Modal de acompanhamento dos batches de geração via IA em tempo real.
 *
 * Faz polling em GET /api/v1/batch_generations a cada POLL_INTERVAL.
 * Para enquanto a modal estiver fechada.
 */
export default function BatchProgressModal({ open, onOpenChange, highlightId }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [activeTab, setActiveTab] = useState('active');

  const load = useCallback(async () => {
    try {
      const json = await batchGenerationsApi.index();
      setBatches(json?.data || []);
    } catch (e) {
      toast.error('Erro ao carregar batches');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carrega ao abrir e configura polling enquanto aberta.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setActiveTab('active'); // ao reabrir, volta para "Em andamento"
    load();
    const id = setInterval(load, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [open, load]);

  const handleCancel = async deleteGenerated => {
    if (!cancelTarget) return;
    setCanceling(true);
    try {
      const json = await batchGenerationsApi.cancel(cancelTarget.id, { deleteGenerated });
      const removed = json?.meta?.deleted_questions || 0;
      toast.success(
        deleteGenerated
          ? `Batch cancelado e ${removed} questão(ões) removida(s).`
          : 'Batch cancelado. Questões já geradas foram mantidas.'
      );
      setCancelTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.[0]?.title || 'Falha ao cancelar.');
    } finally {
      setCanceling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary-dark">
            <FiActivity /> Batches em andamento
          </DialogTitle>
          <DialogDescription>
            Os batches são executados em background. Esta lista atualiza a cada 3 segundos.
            {batches.some(b => ACTIVE_STATUSES.includes(b.attributes.status)) && (
              <span className="ml-2 text-warning-dark">● ativo</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && batches.length === 0 ? (
          <p className="py-4 text-center text-sm text-neutral">Carregando...</p>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              {TAB_DEFS.map(t => {
                const count = batches.filter(b => t.statuses.includes(b.attributes.status)).length;
                return (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label} {count > 0 && <span className="ml-1 text-xs opacity-70">({count})</span>}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {TAB_DEFS.map(t => {
              const list = batches.filter(b => t.statuses.includes(b.attributes.status));
              return (
                <TabsContent key={t.value} value={t.value}>
                  {list.length === 0 ? (
                    <p className="py-4 text-center text-sm text-neutral">
                      {t.value === 'active'
                        ? 'Nenhum batch em andamento. Use "Gerar batch" para iniciar.'
                        : `Nenhum batch nesta aba.`}
                    </p>
                  ) : (
                    <ul className="max-h-[55vh] space-y-3 overflow-y-auto pr-2">
                      {list.map(b => (
                        <BatchCard
                          key={b.id}
                          batch={b}
                          highlighted={highlightId && String(b.id) === String(highlightId)}
                          onCancel={() =>
                            setCancelTarget({ id: b.id, items_generated: b.attributes.items_generated })
                          }
                        />
                      ))}
                    </ul>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </div>
      </DialogContent>

      {/* Confirmação de cancelamento — pergunta o que fazer com as questões já geradas */}
      <Dialog open={Boolean(cancelTarget)} onOpenChange={v => !v && !canceling && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-danger">
              <FiStopCircle /> Cancelar batch #{cancelTarget?.id}
            </DialogTitle>
            <DialogDescription>
              {cancelTarget?.items_generated > 0 ? (
                <>
                  Este batch já gerou <strong>{cancelTarget.items_generated} questão(ões)</strong>.
                  O que fazer com elas?
                </>
              ) : (
                'O batch será interrompido. Nenhuma questão foi gerada ainda.'
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelTarget(null)}
              disabled={canceling}
            >
              Voltar
            </Button>
            {cancelTarget?.items_generated > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleCancel(false)}
                  disabled={canceling}
                >
                  Manter questões geradas
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={() => handleCancel(true)}
                  disabled={canceling}
                >
                  Excluir questões geradas
                </Button>
              </>
            )}
            {!cancelTarget?.items_generated && (
              <Button
                variant="outline-danger"
                onClick={() => handleCancel(false)}
                disabled={canceling}
              >
                Confirmar cancelamento
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function BatchCard({ batch, highlighted, onCancel }) {
  const a = batch.attributes;
  const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.queued;
  return (
    <li
      className={`rounded-lg border p-3 ${
        highlighted ? 'border-primary bg-primary/5' : 'border-neutral-lightest bg-white'
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge variant={cfg.variant} className="flex items-center gap-1">
          {cfg.icon} {cfg.label}
        </Badge>
        {a.etapa_label && (
          <Badge variant="primary">{a.etapa_label}</Badge>
        )}
        <code className="text-xs text-neutral">#{batch.id}</code>
        <span className="text-xs text-neutral">{a.generator_model}</span>
        {a.reasoning_effort && (
          <span className="text-xs text-neutral">· effort: {a.reasoning_effort}</span>
        )}
        {(a.descriptor_codes || []).length > 0 && (
          <span className="text-xs text-neutral">· {a.descriptor_codes.length} descritor(es)</span>
        )}
        {a.alternatives_count && (
          <span className="text-xs text-neutral">· {a.alternatives_count} alt</span>
        )}
        <span className="ml-auto text-xs text-neutral">
          Solicitado em {formatDateTime(a.created_at)}
        </span>
      </div>

      {/* Barra de progresso */}
      <div className="mb-2">
        <div className="mb-1 flex justify-between text-xs">
          <span>
            {a.items_generated} / {a.items_total_expected ?? '?'} itens
            {a.status === 'running' && elapsedMinutes(a.started_at) != null && (
              <span className="ml-2 text-neutral">
                ({elapsedMinutes(a.started_at)} min decorridos)
              </span>
            )}
          </span>
          <span className="tabular-nums font-medium">
            {a.progress_pct ?? 0}%
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded bg-neutral-lightest">
          <div
            className={`h-full transition-all ${progressBarClass(a.status)}`}
            style={{ width: `${Math.min(100, a.progress_pct ?? 0)}%` }}
          />
        </div>
      </div>

      {/* Custo e requester */}
      <div className="flex flex-wrap gap-4 text-xs text-neutral">
        <span>
          Custo acumulado: <strong className="text-foreground">${Number(a.total_cost_usd).toFixed(4)}</strong>
        </span>
        {a.requester?.name && <span>por {a.requester.name}</span>}
        {a.finished_at && (
          <span>
            {a.status === 'canceled' ? 'Cancelado' : 'Concluído'} em {formatDateTime(a.finished_at)}
          </span>
        )}
      </div>

      {a.error_message && (
        <details className="mt-2 rounded bg-danger/5 p-2 text-xs">
          <summary className="cursor-pointer text-danger">Detalhes do erro</summary>
          <pre className="mt-1 whitespace-pre-wrap break-words">{a.error_message}</pre>
        </details>
      )}

      {ACTIVE_STATUSES.includes(a.status) && (
        <div className="mt-3 flex justify-end">
          <Button variant="outline-danger" size="xs" onClick={onCancel}>
            <ButtonIcon><FiStopCircle /></ButtonIcon> Cancelar
          </Button>
        </div>
      )}
    </li>
  );
}

function progressBarClass(status) {
  switch (status) {
    case 'done':    return 'bg-success';
    case 'failed':  return 'bg-danger';
    case 'running': return 'bg-warning animate-pulse';
    case 'queued':  return 'bg-neutral-light';
    default:        return 'bg-neutral-light';
  }
}
